import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { API_BASE } from '@/lib/api-base';
import { ApiError } from '@/lib/api-client';
import { ReasonDialog } from '@/pages/platform/common';
import {
  AdminAccessTab,
  ResetPasswordsDialog,
  ResetResultsDialog,
  type ResetResult,
  type ResetTarget,
} from '@/pages/platform/AdminAccessTab';
import { InboxTab, type PlatformNotification } from '@/pages/platform/InboxTab';
import {
  LimitsSettingsTab,
  WorkspaceLimits,
  seatLabel,
  type LimitMode,
} from '@/pages/platform/LimitsTab';
import { OperatorsTab } from '@/pages/platform/OperatorsTab';
import { DefaultsTab } from '@/pages/platform/DefaultsTab';
import { SignupSettingsTab } from '@/pages/platform/SignupSettingsTab';
import { UknowvaOnboardingSettings } from '@/pages/platform/OnboardingSettings';
import { SignupsTab } from '@/pages/platform/SignupsTab';
import { StorageTab } from '@/pages/platform/StorageTab';

/**
 * Platform console — the fleet, from above.
 *
 * Separate credential store (`platform_admins` in the Master DB), separate JWT
 * audience, and no access to any client's payroll data: it reports lifecycle,
 * inventory and audit, and it can hand over a workspace's DATABASE password.
 * Sign-in passwords are bcrypt hashes and are not readable here or anywhere —
 * the console lists accounts and can reset one, which is the safe form of the
 * same operational need.
 *
 * The token lives in sessionStorage, not localStorage: a fleet-wide credential
 * should not outlive the tab it was typed into.
 */

const TOKEN_KEY = 'ukn_platform_token';

interface Inventory {
  entities?: number;
  employees?: number;
  // People the workspace still employs — what a seat limit counts. Leavers
  // keep their payroll history but not their seat, so this is below
  // `employees` for any workspace that has ever had one.
  seats?: number;
  users?: number;
  active_users?: number;
  staff_users?: number;
  password_users?: number;
  payroll_runs?: number;
  error?: string;
}

interface TenantRow {
  tenant_key: string;
  display_name: string;
  instance_url: string;
  instance_host: string;
  // HRMS: onboarded by a uKnowva instance. SIGNUP: created by self-service signup.
  origin?: 'HRMS' | 'SIGNUP';
  status: string;
  is_active: boolean;
  db_name: string;
  db_host: string;
  db_port: number;
  db_user: string;
  db_size_bytes: number | null;
  schema_version: string | null;
  schema_drift: boolean;
  sso_configured: boolean;
  // Issued at setup but never collected: uKnowva sign-ins fail until it is.
  sso_key_pending?: boolean;
  hrms_connected: boolean;
  field_map_confirmed: boolean;
  created_at: string | null;
  last_seen_at: string | null;
  suspended_reason: string | null;
  employee_mode?: LimitMode;
  // null = no cap on this workspace.
  employee_limit?: number | null;
  inventory?: Inventory;
}

interface Overview {
  generated_at: string;
  head_revision: string | null;
  totals: Record<string, number>;
  tenants: TenantRow[];
  requests_by_status: Record<string, number>;
  open_requests: {
    request_id: string;
    reference: string;
    instance: string;
    status: string;
    created_at: string | null;
    error: string | null;
  }[];
  orphan_databases: string[];
}

interface TenantUser {
  username: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  has_password: boolean;
  sso_provider: string | null;
  emp_id: string | null;
  created_at: string | null;
  platform_lock?: { reason: string; locked_by: string; locked_at: string | null } | null;
}

interface PasswordLogin {
  enabled: boolean;
  reachable: boolean;
  login_path: string;
  updated_by: string | null;
  updated_at: string | null;
}

type MainTab =
  | 'workspaces'
  | 'admins'
  | 'signups'
  | 'inbox'
  | 'operators'
  | 'limits'
  | 'payitems'
  | 'settings'
  | 'storage';

function bytes(n: number | null | undefined): string {
  if (!n) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  SYNCING: 'warning',
  PROVISIONING: 'warning',
  PENDING_APPROVAL: 'warning',
  SUSPENDED: 'warning',
  DISABLED: 'error',
  FAILED: 'error',
  ARCHIVED: 'default',
};

export function PlatformConsolePage() {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [mainTab, setMainTab] = useState<MainTab>('workspaces');
  const [unread, setUnread] = useState(0);
  const [pendingSignups, setPendingSignups] = useState(0);
  const [meId, setMeId] = useState<string | null>(null);
  const [focusSignup, setFocusSignup] = useState<string | null>(null);
  const [requestAction, setRequestAction] = useState<
    { id: string; reference: string; instance: string } | null
  >(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [setupLink, setSetupLink] = useState<
    { reference: string; instance: string; url: string; expires_at: string } | null
  >(null);

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const res = await fetch(`${API_BASE}/platform${path}`, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token ?? ''}`,
        },
      });
      if (res.status === 401) {
        try {
          sessionStorage.removeItem(TOKEN_KEY);
        } catch {
          /* private mode — the token was never stored */
        }
        setToken(null);
        throw new Error('Your console session expired. Sign in again.');
      }
      if (!res.ok) {
        // Keeps the body so a storage form can put the error under the field it names.
        throw new ApiError(res.status, await res.json().catch(() => ({})));
      }
      return res.status === 204 ? undefined : res.json();
    },
    [token],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      setData((await call('/overview')) as Overview);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [token, call]);

  useEffect(() => {
    void load();
  }, [load]);

  // The bell and the Signups badge. Quiet on failure: a Master DB that has
  // not been migrated for signup yet must not break the fleet view.
  const loadBadges = useCallback(async () => {
    if (!token) return;
    try {
      const n = (await call('/notifications?unread_only=true&limit=1')) as { unread: number };
      setUnread(n.unread);
    } catch {
      /* feature not migrated yet */
    }
    try {
      const sgn = (await call('/signups?status=PENDING_APPROVAL')) as { signups: unknown[] };
      setPendingSignups(sgn.signups.length);
    } catch {
      /* feature not migrated yet */
    }
  }, [token, call]);

  useEffect(() => {
    if (!token) return;
    void loadBadges();
    call('/me').then((m) => setMeId((m as { id: string }).id)).catch(() => undefined);
    const t = window.setInterval(() => void loadBadges(), 30000);
    return () => window.clearInterval(t);
  }, [token, call, loadBadges]);

  const openNotification = (n: PlatformNotification) => {
    if (n.ref_type === 'signup' && n.ref_id) {
      setFocusSignup(n.ref_id);
      setMainTab('signups');
    } else {
      setMainTab('workspaces');
      const t = data?.tenants.find((x) => x.tenant_key === n.tenant_key);
      if (t) setSelected(t);
    }
  };

  const approveRequest = async (id: string) => {
    try {
      await call(`/provisioning-requests/${id}/approve`, { method: 'POST' });
      setNotice('Approved. The workspace is being created.');
      await load();
      void loadBadges();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const retryRequest = async (id: string, reference: string) => {
    try {
      await call(`/provisioning-requests/${id}/retry`, { method: 'POST' });
      setNotice(`Retrying ${reference}. Steps that already finished are kept.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // For a client who lost the browser setup started in. Single use, and
  // whoever opens it first takes the setup over.
  const issueSetupLink = async (id: string) => {
    try {
      setSetupLink(
        (await call(`/provisioning-requests/${id}/wizard-link`, { method: 'POST' })) as {
          reference: string; instance: string; url: string; expires_at: string;
        },
      );
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const signIn = async () => {
    setBusy(true);
    setAuthError(null);
    try {
      const res = await fetch(`${API_BASE}/platform/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      const body = (await res.json()) as { access_token?: string; token?: string };
      const t = body.access_token ?? body.token ?? '';
      if (!t) throw new Error('The server returned no token.');
      try {
        sessionStorage.setItem(TOKEN_KEY, t);
      } catch {
        /* private mode — hold it in memory for this tab only */
      }
      setToken(t);
      setPassword('');
    } catch (err) {
      setAuthError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing stored */
    }
    setToken(null);
    setData(null);
  };

  if (!token) {
    return (
      <Shell>
        <Paper sx={{ maxWidth: 420, width: '100%', p: 4 }} elevation={2}>
          <Typography variant="h6" gutterBottom>
            Platform console
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fleet operations. Separate credentials from any workspace. A client
            administrator's sign-in does not open this.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Email"
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              fullWidth
            />
            <TextField
              label="Password"
              size="small"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void signIn();
              }}
              autoComplete="current-password"
              fullWidth
            />
            {authError && <Alert severity="error">{authError}</Alert>}
            <Button
              variant="contained"
              onClick={() => void signIn()}
              disabled={busy || !email.trim() || !password}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              No account? Create one on the server:
              <br />
              <code>py -m app.tenancy.cli create-platform-admin --email you@co</code>
            </Typography>
          </Stack>
        </Paper>
      </Shell>
    );
  }

  return (
    <Shell wide>
      <Box sx={{ width: '100%', maxWidth: 1400 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }} spacing={2}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            Platform console
          </Typography>
          <Tooltip title={unread ? `${unread} unread notification(s)` : 'Notifications'}>
            <IconButton size="small" onClick={() => setMainTab('inbox')} aria-label="Notifications">
              <Badge badgeContent={unread} color="error" max={99}>
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Button size="small" onClick={() => { void load(); void loadBadges(); }}>
            Refresh
          </Button>
          <Button size="small" onClick={signOut}>
            Sign out
          </Button>
        </Stack>

        <Tabs
          value={mainTab}
          onChange={(_e, v) => setMainTab(v as MainTab)}
          variant="scrollable"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="workspaces" label="Workspaces" />
          <Tab value="admins" label="Admin access" />
          <Tab
            value="signups"
            label={
              <Badge badgeContent={pendingSignups} color="warning" sx={{ pr: pendingSignups ? 1.5 : 0 }}>
                Signups
              </Badge>
            }
          />
          <Tab
            value="inbox"
            label={
              <Badge badgeContent={unread} color="error" sx={{ pr: unread ? 1.5 : 0 }}>
                Inbox
              </Badge>
            }
          />
          <Tab value="operators" label="Operators" />
          <Tab value="limits" label="Limits" />
          <Tab value="payitems" label="Defaults" />
          <Tab value="settings" label="Onboarding & signup" />
          <Tab value="storage" label="Storage" />
        </Tabs>

        {mainTab === 'signups' && (
          <SignupsTab call={call} focusId={focusSignup} onChanged={() => { void load(); void loadBadges(); }} />
        )}
        {mainTab === 'inbox' && (
          <InboxTab call={call} onChanged={() => void loadBadges()} onOpen={openNotification} />
        )}
        {mainTab === 'admins' && <AdminAccessTab call={call} />}
        {mainTab === 'operators' && <OperatorsTab call={call} meId={meId} />}
        {mainTab === 'limits' && <LimitsSettingsTab call={call} />}
        {mainTab === 'payitems' && <DefaultsTab call={call} />}
        {mainTab === 'settings' && (
          <>
            <UknowvaOnboardingSettings call={call} />
            <SignupSettingsTab call={call} />
          </>
        )}
        {mainTab === 'storage' && <StorageTab call={call} tenants={data?.tenants ?? []} />}

        {mainTab === 'workspaces' && notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {mainTab === 'workspaces' && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {mainTab === 'workspaces' && !data && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {mainTab === 'workspaces' && data && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Metric label="Workspaces" value={data.totals.tenants} />
              <Metric label="Active" value={data.totals.active} tone="success" />
              <Metric
                label="Unfinished"
                value={data.totals.unfinished}
                tone={data.totals.unfinished ? 'warning' : undefined}
              />
              <Metric label="Entities" value={data.totals.entities} />
              <Metric label="Employees" value={data.totals.employees} />
              <Metric
                label="Over seat limit"
                value={data.totals.over_limit}
                tone={data.totals.over_limit ? 'warning' : undefined}
              />
              <Metric label="Sign-ins" value={data.totals.users} />
              <Metric label="Disk" value={bytes(data.totals.db_bytes)} />
              <Metric
                label="Schema drift"
                value={data.totals.schema_drift}
                tone={data.totals.schema_drift ? 'error' : undefined}
              />
            </Stack>

            <Paper variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
              <TableContainer><Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Instance</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Entities</TableCell>
                    <TableCell align="right">Employees</TableCell>
                    <TableCell align="right">Seats</TableCell>
                    <TableCell align="right">Sign-ins</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Schema</TableCell>
                    <TableCell>Wiring</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.tenants.map((t) => (
                    <TableRow
                      key={t.tenant_key}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelected(t)}
                    >
                      <TableCell>
                        <Typography variant="body2">{t.tenant_key}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.db_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{t.origin === 'SIGNUP' ? '—' : t.instance_host}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t.origin === 'SIGNUP' ? 'Signup' : 'uKnowva'}
                          color={t.origin === 'SIGNUP' ? 'info' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t.status}
                          color={STATUS_COLOR[t.status] ?? 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{t.inventory?.entities ?? '—'}</TableCell>
                      <TableCell align="right">{t.inventory?.employees ?? '—'}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={
                            t.employee_limit != null &&
                            (t.inventory?.seats ?? 0) > t.employee_limit
                              ? 'error'
                              : 'inherit'
                          }
                        >
                          {seatLabel(t.inventory?.seats ?? null, t.employee_limit ?? null)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{t.inventory?.users ?? '—'}</TableCell>
                      <TableCell align="right">{t.inventory?.payroll_runs ?? '—'}</TableCell>
                      <TableCell align="right">{bytes(t.db_size_bytes)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color={t.schema_drift ? 'error' : 'text.secondary'}
                        >
                          {t.schema_version ?? '—'}
                          {t.schema_drift ? ' (drift)' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {t.sso_configured && !t.sso_key_pending && (
                            <Chip size="small" label="SSO" variant="outlined" />
                          )}
                          {t.sso_key_pending && (
                            <Tooltip title="The uKnowva sign-in key was never collected from setup. Issue a new one under Credentials.">
                              <Chip size="small" label="SSO key not collected" color="warning" variant="outlined" />
                            </Tooltip>
                          )}
                          {t.hrms_connected && <Chip size="small" label="HRMS" variant="outlined" />}
                          {t.field_map_confirmed && (
                            <Chip size="small" label="mapped" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </Paper>

            {data.open_requests.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Onboarding still in flight
                </Typography>
                <Stack divider={<Divider flexItem />}>
                  {data.open_requests.map((r) => (
                    <Box key={r.request_id} sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {r.reference} · {r.instance} · {r.status}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.error ?? `started ${r.created_at ?? 'unknown'}`}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        {r.status === 'PENDING_APPROVAL' && (
                          <>
                            <Button size="small" variant="contained" onClick={() => void approveRequest(r.request_id)}>
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setRequestAction({ id: r.request_id, reference: r.reference, instance: r.instance })}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {r.status === 'FAILED' && (
                          <Button size="small" variant="contained" onClick={() => void retryRequest(r.request_id, r.reference)}>
                            Retry
                          </Button>
                        )}
                        <Tooltip title="One-time link that reopens this setup in another browser">
                          <Button size="small" onClick={() => void issueSetupLink(r.request_id)}>
                            Setup link
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            <Dialog open={!!setupLink} onClose={() => setSetupLink(null)} fullWidth maxWidth="sm">
              <DialogTitle>Setup link for {setupLink?.reference}</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Works once, until {setupLink?.expires_at}. Whoever opens it first takes
                  over setup for {setupLink?.instance}, and the browser that had it loses
                  it. Send it only to the client's confirmed administrator.
                </Alert>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  <code style={{ userSelect: 'all' }}>{setupLink?.url}</code>
                </Typography>
                <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    onClick={() => {
                      if (setupLink) void navigator.clipboard?.writeText(setupLink.url).catch(() => undefined);
                    }}
                  >
                    Copy
                  </Button>
                  <Button variant="contained" onClick={() => setSetupLink(null)}>Done</Button>
                </Stack>
              </DialogContent>
            </Dialog>

            {data.orphan_databases.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {data.orphan_databases.length} database(s) on disk with no registry
                row. Left behind by a run that was cleared by hand:{' '}
                {data.orphan_databases.join(', ')}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              Schema head {data.head_revision ?? '—'} · generated {data.generated_at}
            </Typography>
          </>
        )}

        {selected && (
          <TenantDialog
            tenant={selected}
            call={call}
            onClose={() => setSelected(null)}
            onChanged={() => void load()}
          />
        )}

        <ReasonDialog
          open={!!requestAction}
          title={`Reject ${requestAction?.reference ?? ''}?`}
          description={`No workspace is created for ${requestAction?.instance ?? ''}.`}
          confirmLabel="Reject"
          danger
          onClose={() => setRequestAction(null)}
          onConfirm={async (reason) => {
            if (!requestAction) return;
            await call(`/provisioning-requests/${requestAction.id}/reject`, {
              method: 'POST', body: JSON.stringify({ reason }),
            });
            setNotice(`Rejected ${requestAction.reference}.`);
            await load();
          }}
        />
      </Box>
    </Shell>
  );
}

function Shell({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  // The theme pins html/body to the viewport with `overflow: hidden` for the
  // AppShell's sake, so a standalone page has to own its own scrolling.
  return (
    <Box
      sx={{
        height: '100vh',
        overflowY: 'auto',
        display: 'flex',
        alignItems: wide ? 'flex-start' : 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: { xs: 2, md: 3 },
      }}
    >
      {children}
    </Box>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  // Totals arrive as an open record, so a key the server has not sent yet
  // reads as undefined rather than crashing the whole fleet view.
  value: number | string | undefined;
  tone?: 'success' | 'warning' | 'error';
}) {
  const bg =
    tone === 'success'
      ? 'success.50'
      : tone === 'warning'
        ? 'warning.50'
        : tone === 'error'
          ? 'error.50'
          : 'background.paper';
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 120, bgcolor: bg }}>
      <Typography variant="h6">{value ?? '—'}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function TenantDialog({
  tenant,
  call,
  onClose,
  onChanged,
}: {
  tenant: TenantRow;
  call: (path: string, init?: RequestInit) => Promise<unknown>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState(0);
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose} scroll="paper">
      <DialogTitle sx={{ pb: 0 }}>
        {tenant.tenant_key}
        <Typography variant="body2" color="text.secondary">
          {tenant.origin === 'SIGNUP'
            ? `${tenant.display_name} · standalone workspace (self-service signup)`
            : tenant.instance_url}
        </Typography>
        <Tabs value={tab} onChange={(_e, v) => setTab(v as number)} sx={{ mt: 1 }}>
          <Tab label="Inventory" />
          <Tab label="Plan & limits" />
          <Tab label="Credentials" />
          <Tab label="Sign-ins" />
          <Tab label="Audit" />
        </Tabs>
      </DialogTitle>
      <DialogContent dividers>
        {tab === 0 && <InventoryTab tenant={tenant} call={call} onChanged={onChanged} />}
        {tab === 1 && (
          <WorkspaceLimits tenantKey={tenant.tenant_key} call={call} onChanged={onChanged} />
        )}
        {tab === 2 && <CredentialsTab tenant={tenant} call={call} />}
        {tab === 3 && <UsersTab tenant={tenant} call={call} />}
        {tab === 4 && <AuditTab tenant={tenant} call={call} />}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
        {k}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
        {v}
      </Typography>
    </Box>
  );
}

function InventoryTab({
  tenant,
  call,
  onChanged,
}: {
  tenant: TenantRow;
  call: (path: string, init?: RequestInit) => Promise<unknown>;
  onChanged: () => void;
}) {
  const inv = tenant.inventory ?? {};
  const [status, setStatus] = useState(tenant.status);
  // Which reason dialog is open: a temporary hold, or the end of a contract.
  const [confirm, setConfirm] = useState<'suspend' | 'disable' | 'reinstate' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const suspended = status === 'SUSPENDED';
  const disabled = status === 'DISABLED';
  const stopped = suspended || disabled;

  const resume = async () => {
    setErr(null);
    try {
      await call(`/tenants/${tenant.tenant_key}/resume`, { method: 'POST' });
      setStatus('ACTIVE');
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  // Suspend, disable and reinstate all take a reason and all land on a
  // status — one handler, so the three cannot drift apart.
  const act = async (path: string, reason: string, next: string) => {
    await call(`/tenants/${tenant.tenant_key}/${path}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    setStatus(next);
    onChanged();
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            {disabled
              ? 'This workspace is disabled permanently. Every sign-in is refused. The database, the payroll history and every account are still here.'
              : suspended
                ? 'This workspace is suspended. Every sign-in and every open session is refused.'
                : 'Stopping a workspace refuses every sign-in and ends open sessions within seconds. No data is changed either way.'}
          </Typography>
          {stopped && tenant.suspended_reason && (
            <Typography variant="caption" color="text.secondary">Reason: {tenant.suspended_reason}</Typography>
          )}
        </Box>
        {disabled ? (
          <Button variant="contained" size="small" onClick={() => setConfirm('reinstate')}>
            Reinstate
          </Button>
        ) : suspended ? (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={() => void resume()}>Resume</Button>
            <Button variant="outlined" color="error" size="small"
              onClick={() => setConfirm('disable')}>
              Disable permanently
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="warning" size="small"
              onClick={() => setConfirm('suspend')} disabled={status === 'ARCHIVED'}>
              Suspend
            </Button>
            <Button variant="outlined" color="error" size="small"
              onClick={() => setConfirm('disable')} disabled={status === 'ARCHIVED'}>
              Disable permanently
            </Button>
          </Stack>
        )}
      </Paper>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {/* Two stop buttons for two different decisions. Suspend is the one to
          reach for while something is being sorted out — Resume undoes it.
          Disable is for a contract that has ended: the same effect on the
          running system, but Resume refuses it, so nobody reverses an
          end-of-contract by clicking the billing-hold button. Neither
          deletes anything. */}
      <ReasonDialog
        open={confirm === 'suspend'}
        title={`Suspend ${tenant.tenant_key}?`}
        description="Everyone in this workspace is signed out and cannot sign in until you resume it. Nothing is deleted."
        confirmLabel="Suspend"
        danger
        onClose={() => setConfirm(null)}
        onConfirm={(reason) => act('suspend', reason, 'SUSPENDED')}
      />
      <ReasonDialog
        open={confirm === 'disable'}
        title={`Disable ${tenant.tenant_key} permanently?`}
        description={
          <>
            For a client who has left. Everyone is signed out and cannot sign in, and
            <strong> Resume will not bring this workspace back</strong>. Only Reinstate,
            which asks for a reason of its own. Nothing is deleted: the database, the payroll
            history and every account stay exactly as they are.
          </>
        }
        confirmLabel="Disable permanently"
        danger
        onClose={() => setConfirm(null)}
        onConfirm={(reason) => act('disable', reason, 'DISABLED')}
      />
      <ReasonDialog
        open={confirm === 'reinstate'}
        title={`Reinstate ${tenant.tenant_key}?`}
        description="This workspace was disabled permanently. Bringing it back puts it straight into service. Everyone can sign in again."
        confirmLabel="Reinstate"
        onClose={() => setConfirm(null)}
        onConfirm={(reason) => act('reinstate', reason, 'ACTIVE')}
      />
      <Row k="Origin" v={tenant.origin === 'SIGNUP' ? 'Self-service signup (standalone)' : 'uKnowva onboarding'} />
      <Row k="Status" v={status} />
      <Row k="Database" v={`${tenant.db_name} on ${tenant.db_host}:${tenant.db_port}`} />
      <Row k="Database role" v={tenant.db_user} />
      <Row k="Size on disk" v={bytes(tenant.db_size_bytes)} />
      <Row k="Schema" v={`${tenant.schema_version ?? '—'}${tenant.schema_drift ? ' (drift)' : ''}`} />
      <Row k="Created" v={tenant.created_at ?? '—'} />
      <Divider sx={{ my: 1.5 }} />
      {inv.error ? (
        <Alert severity="warning">Could not read this workspace: {inv.error}</Alert>
      ) : (
        <>
          <Row k="Entities" v={inv.entities ?? '—'} />
          <Row k="Employees" v={inv.employees ?? '—'} />
          <Row
            k="Sign-in accounts"
            v={`${inv.users ?? 0} (${inv.staff_users ?? 0} staff, ${
              (inv.users ?? 0) - (inv.password_users ?? 0)
            } SSO-only)`}
          />
          <Row k="Payroll runs" v={inv.payroll_runs ?? '—'} />
        </>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Row k="SSO key installed" v={tenant.sso_configured ? 'yes' : 'no'} />
      <Row k="HRMS token stored" v={tenant.hrms_connected ? 'yes' : 'no'} />
      <Row k="Field map confirmed" v={tenant.field_map_confirmed ? 'yes' : 'no'} />
    </Box>
  );
}

function CredentialsTab({
  tenant,
  call,
}: {
  tenant: TenantRow;
  call: (path: string, init?: RequestInit) => Promise<unknown>;
}) {
  const [reason, setReason] = useState('');
  const [creds, setCreds] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reveal = async () => {
    setBusy(true);
    setErr(null);
    try {
      setCreds(
        (await call(`/tenants/${tenant.tenant_key}/credentials`, {
          method: 'POST',
          body: JSON.stringify({ reason }),
        })) as Record<string, unknown>,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        This reveals the workspace's <strong>database</strong> password. Generated
        at provisioning, stored encrypted, and never typed by anyone. The read is
        recorded against your name. Sign-in passwords are bcrypt hashes and cannot
        be revealed; use the Sign-ins tab to reset one.
      </Alert>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <TextField
          label="Why do you need it?"
          size="small"
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Recorded in the audit trail"
        />
        <Button variant="contained" onClick={() => void reveal()} disabled={busy || reason.trim().length < 3}>
          {busy ? 'Reading…' : 'Reveal'}
        </Button>
      </Stack>
      {err && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {err}
        </Alert>
      )}
      {creds && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Row k="Host" v={`${creds.db_host}:${creds.db_port}`} />
          <Row k="Database" v={String(creds.db_name)} />
          <Row k="User" v={String(creds.db_user)} />
          <Row
            k="Password"
            v={<code style={{ userSelect: 'all' }}>{String(creds.db_password)}</code>}
          />
          <Row k="psql" v={<code style={{ userSelect: 'all' }}>{String(creds.psql)}</code>} />
          <Row k="SSO key fingerprint" v={String(creds.sso_secret_fingerprint ?? '—')} />
        </Paper>
      )}
      {tenant.origin !== 'SIGNUP' && <SsoKeySection tenant={tenant} call={call} />}
    </Box>
  );
}

/**
 * The console twin of `cli rotate-sso-secret`: for a sign-in key that was
 * never collected from setup (the browser was lost) or may have leaked.
 */
function SsoKeySection({
  tenant,
  call,
}: {
  tenant: TenantRow;
  call: (path: string, init?: RequestInit) => Promise<unknown>;
}) {
  const [confirm, setConfirm] = useState(false);
  const [issued, setIssued] = useState<{ sso_shared_secret: string; where: string } | null>(null);

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" gutterBottom>uKnowva sign-in key</Typography>
      {tenant.sso_key_pending ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The key issued at setup was never collected, so sign-in from{' '}
          {tenant.instance_host} fails. Issue a new one and send it to the client's
          uKnowva administrator.
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Issuing a new key stops every sign-in from {tenant.instance_host} until it is
          pasted into the Payroll APIs plugin there.
        </Typography>
      )}
      <Button
        variant={tenant.sso_key_pending ? 'contained' : 'outlined'}
        color="warning"
        size="small"
        onClick={() => setConfirm(true)}
      >
        Issue new sign-in key
      </Button>
      {issued && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            Shown once. Paste it into {issued.where}.
          </Alert>
          <Row k="New key" v={<code style={{ userSelect: 'all' }}>{issued.sso_shared_secret}</code>} />
        </Paper>
      )}
      <ReasonDialog
        open={confirm}
        title="Issue a new sign-in key?"
        description={`Sign-in from ${tenant.instance_host} fails until the new key is installed in its uKnowva Payroll APIs plugin. The old key stops working now.`}
        confirmLabel="Issue key"
        danger
        onClose={() => setConfirm(false)}
        onConfirm={async (reason) => {
          setIssued(
            (await call(`/tenants/${tenant.tenant_key}/sso-key/rotate`, {
              method: 'POST',
              body: JSON.stringify({ reason }),
            })) as { sso_shared_secret: string; where: string },
          );
        }}
      />
    </Box>
  );
}

function UsersTab({
  tenant,
  call,
}: {
  tenant: TenantRow;
  call: (path: string, init?: RequestInit) => Promise<unknown>;
}) {
  const [users, setUsers] = useState<TenantUser[] | null>(null);
  const [pwLogin, setPwLogin] = useState<PasswordLogin | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<ResetTarget[] | null>(null);
  const [results, setResults] = useState<ResetResult[] | null>(null);
  const [toggle, setToggle] = useState<{ user: TenantUser; disable: boolean } | null>(null);
  const [pwToggle, setPwToggle] = useState<boolean | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      const body = (await call(`/tenants/${tenant.tenant_key}/users`)) as {
        users: TenantUser[];
        password_login?: PasswordLogin;
      };
      setUsers(body.users);
      setPwLogin(body.password_login ?? null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [tenant.tenant_key, call]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  if (err) return <Alert severity="error">{err}</Alert>;
  if (!users) return <CircularProgress size={24} />;

  const standalone = tenant.origin === 'SIGNUP';

  return (
    <Box>
      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}
      {pwLogin && (
        <Alert
          severity={pwLogin.reachable ? 'info' : 'warning'}
          sx={{ mb: 2 }}
          action={!standalone && (
            <Button color="inherit" size="small" onClick={() => setPwToggle(!pwLogin.enabled)}>
              {pwLogin.enabled ? 'Turn off' : 'Turn on'}
            </Button>
          )}
        >
          {pwLogin.reachable ? (
            <>
              Password sign-in is open: Workspace ID <strong>{tenant.tenant_key}</strong> on the login page
              {pwLogin.enabled && pwLogin.updated_by ? ` (opened by ${pwLogin.updated_by})` : ''}.
            </>
          ) : (
            <>
              This workspace signs in only through uKnowva. A reset password cannot be used until
              password sign-in is turned on.
            </>
          )}
        </Alert>
      )}
      <ReasonDialog
        open={pwToggle !== null}
        title={pwToggle ? 'Turn on password sign-in?' : 'Turn off password sign-in?'}
        description={pwToggle
          ? `Anyone in ${tenant.tenant_key} with a password can sign in at payroll’s login page with Workspace ID “${tenant.tenant_key}”. uKnowva sign-in keeps working.`
          : 'The login page stops checking passwords for this workspace. Nobody’s password is changed, and uKnowva sign-in keeps working.'}
        confirmLabel={pwToggle ? 'Turn on' : 'Turn off'}
        onClose={() => setPwToggle(null)}
        onConfirm={async (reason) => {
          await call(`/tenants/${tenant.tenant_key}/password-login`, {
            method: 'PUT', body: JSON.stringify({ enabled: pwToggle, reason }),
          });
          await loadUsers();
        }}
      />
      <ResetPasswordsDialog
        targets={resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={async (reason, allowPasswordLogin, newPassword) => {
          const username = resetTarget?.[0]?.username;
          if (!username) return;
          const body = (await call(
            `/tenants/${tenant.tenant_key}/users/${encodeURIComponent(username)}/reset-password`,
            {
              method: 'POST',
              body: JSON.stringify({
                reason,
                allow_password_login: allowPasswordLogin,
                new_password: newPassword ?? null,
              }),
            },
          )) as ResetResult;
          setResults([{ ...body, ok: true }]);
          await loadUsers();
        }}
      />
      <ResetResultsDialog results={results} onClose={() => setResults(null)} />
      <ReasonDialog
        open={!!toggle}
        title={toggle?.disable ? `Disable ${toggle.user.username}?` : `Re-enable ${toggle?.user.username ?? ''}?`}
        description={toggle?.disable
          ? 'Signed out on their next request and refused at every sign-in (password, uKnowva, Google). The workspace’s own admins cannot switch it back on.'
          : 'Lifts the platform lock and switches the account back on.'}
        confirmLabel={toggle?.disable ? 'Disable' : 'Enable'}
        danger={toggle?.disable}
        onClose={() => setToggle(null)}
        onConfirm={async (reason) => {
          if (!toggle) return;
          const body = (await call(
            `/tenants/${tenant.tenant_key}/users/${encodeURIComponent(toggle.user.username)}/${toggle.disable ? 'disable' : 'enable'}`,
            { method: 'POST', body: JSON.stringify({ reason }) },
          )) as { warning?: string | null };
          setWarning(body.warning ?? null);
          await loadUsers();
        }}
      />
      <TableContainer><Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>Signs in with</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.username} hover>
              <TableCell>{u.username}</TableCell>
              <TableCell>{u.full_name}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={u.role}
                  color={u.role === 'ADMIN' ? 'error' : 'default'}
                />
              </TableCell>
              <TableCell>{u.emp_id ?? '—'}</TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {u.has_password ? 'password' : 'SSO only'}
                  {u.sso_provider ? ` · ${u.sso_provider}` : ''}
                </Typography>
              </TableCell>
              <TableCell>
                {u.platform_lock ? (
                  <Tooltip title={`${u.platform_lock.reason} — ${u.platform_lock.locked_by}`}>
                    <Chip size="small" color="error" variant="outlined" label="Disabled by platform" />
                  </Tooltip>
                ) : (
                  <Chip size="small" variant="outlined" label={u.is_active ? 'Active' : 'Inactive'}
                    color={u.is_active ? 'success' : 'default'} />
                )}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Button
                  size="small"
                  onClick={() => setResetTarget([{
                    tenant_key: tenant.tenant_key,
                    username: u.username,
                    signin_reachable: pwLogin?.reachable ?? true,
                  }])}
                >
                  Reset password
                </Button>
                {u.is_active ? (
                  <Button size="small" color="error" onClick={() => setToggle({ user: u, disable: true })}>
                    Disable
                  </Button>
                ) : (
                  <Button size="small" onClick={() => setToggle({ user: u, disable: false })}>
                    Enable
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></TableContainer>
    </Box>
  );
}

function AuditTab({
  tenant,
  call,
}: {
  tenant: TenantRow;
  call: (path: string, init?: RequestInit) => Promise<unknown>;
}) {
  const [entries, setEntries] = useState<
    { at: string | null; action: string; actor: string | null; detail: unknown }[] | null
  >(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const body = (await call(`/audit?tenant_key=${tenant.tenant_key}&limit=100`)) as {
          entries: { at: string | null; action: string; actor: string | null; detail: unknown }[];
        };
        setEntries(body.entries);
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [tenant.tenant_key, call]);

  if (err) return <Alert severity="error">{err}</Alert>;
  if (!entries) return <CircularProgress size={24} />;
  if (entries.length === 0) return <Typography variant="body2">No audit entries.</Typography>;

  return (
    <Stack divider={<Divider flexItem />}>
      {entries.map((e, i) => (
        <Box key={i} sx={{ py: 1 }}>
          <Typography variant="body2">
            {e.action} · {e.actor ?? 'system'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {e.at} · {JSON.stringify(e.detail)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
